import * as yup from 'yup';
import { useExperiment } from '@web/experiments';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
} from '../../cms';
import { addonSectionSchema } from './addon_sections';
import { addonModalSchema } from './addon_modal';

export const quizNextButtonSchema = yup
  .object({
    desktopCTA: yup.string(),
    mobileCTA: yup.string(),
  })
  .transform(value => {
    let { value: experimentValue } = useExperiment(value.test_name ?? null);
    experimentValue = experimentValue || 'control';
    let ctaVariants = value;
    if (ctaVariants?.component === 'flagship_ab_test') {
      const experimentVariation = ctaVariants.variants.find(
        variant => variant.variant_name === experimentValue,
      );
      ctaVariants = experimentVariation?.blocks[0] ?? [];
    }
    return {
      desktopCTA: ctaVariants.desktopCTA || 'Continue',
      mobileCTA: ctaVariants.mobileCTA || 'Continue',
    };
  });

export const onboardingIntroSchema = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    headline: yup.string(),
    ctaButtonText: yup.string(),
    body: yup.string(),
    ollieLogo: requiredImageFromCMSSchema,
    fontColor: yup.string(),
    popUpText: yup.string().nullable(),
    popUpBgColor: yup.string(),
    popUpAlignment: yup.string(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      headline: value.question[0].headline,
      ctaButtonText: value.ctaButtonText || 'Continue',
      ollieLogo: value.ollieLogo,
      body: value.question[0].body,
      fontColor: value.question[0].fontColor || undefined,
      popUpText: value.question[0].popUpText || null,
      popUpBgColor: value.question[0].popUpBgColor || undefined,
      popUpAlignment: value.question[0].popUpAlignment || undefined,
    };
  });

export const pupName = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    subheadline: yup.string(),
    ctaButtonText: yup.string(),
    pupNameQuestionCaption: yup.string(),
    pupNameQuestionPrompt: yup.string(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      ctaButtonText: value.ctaButtonText || 'Continue',
      subheadline: value.question[0].subheadline,
      pupNameQuestionCaption: value.question?.[0].caption,
      pupNameQuestionPrompt: value.question?.[0].prompt,
      labelName: value.question?.[0].labelName || "Your pup's name",
    };
  });

export const pupGender = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    ctaButtonText: yup.string(),
    pupGenderQuestionSubheadline: yup.string(),
    pupNameQuestionPrompt: yup.string(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      pupGenderQuestionSubheadline: value.question?.[0].subheadline,
      pupGenderQuestionPrompt: value.question?.[0].prompt,
      labelName: value.body?.[0].labelName || "Your pup's gender",
    };
  });

export const pupStatus = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    ctaButtonText: yup.string(),
    pupStatusQuestionSubheadline: yup.string(),
    pupStatusQuestionPrompt: yup.string(),
    unspayedChipLabel: yup.string().required(),
    spayedChipLabel: yup.string().required(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      pupStatusQuestionSubheadline: value.question?.[0].subheadline,
      pupStatusQuestionPrompt: value.question?.[0].prompt,
      labelName:
        value.question?.[0].labelName || "Your pup's spay/neuter status",
      spayedChipLabel: '{{pupStatusTermUppercase}}',
      unspayedChipLabel: 'Not {{pupStatusTermUppercase}}',
    };
  });

export const weight = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    weightLabel: yup.string(),
    idealWeightLabel: yup.string(),
    ctaButtonText: yup.string(),
    weightQuestionSubheadline: yup.string(),
    weightQuestionPrompt: yup.string(),
    puppyIdealWeightLabel: yup.string(),
    idealWeightHelp: yup.string(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      ctaButtonText: value.ctaButtonText || 'Continue',
      weightQuestionSubheadline: value.question?.[0].subheadline,
      weightQuestionPrompt: value.question?.[0].prompt,
      weightLabel:
        value.question?.[0].weightLabel || 'Pup’s current weight in lbs',
      idealWeightLabel:
        value.question?.[0].idealWeightLabel ||
        'Pup’s ideal weight in lbs (optional)',
      puppyIdealWeightLabel:
        value.question?.[0].puppyIdealWeightLabel ||
        'Pup’s ideal weight in lbs (optional)',
      idealWeightHelp:
        "If added, we'll use this weight to determine your pup's daily calories.",
    };
  });

export const activity = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    activityQuestionPrompt: yup.string(),
    subheadline: yup.string(),
    levels: yup
      .array(
        yup.object({
          name: yup.string().required(),
          value: yup.string().required(),
          description: yup.string().required(),
          image: requiredImageFromCMSSchema,
        }),
      )
      .required(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      subheadline: value.question?.[0].subheadline,
      activityQuestionPrompt: value.question?.[0].prompt,
      levels: [
        {
          name: value.question[0].low_activity_card_title,
          value: 'Low',
          image: value.question[0].low_activity_image,
          description: value.question[0].low_activity_card_description,
        },
        {
          name: value.question[0].medium_activity_card_title,
          value: 'Moderate',
          image: value.question[0].medium_activity_image,
          description: value.question[0].medium_activity_card_description,
        },
        {
          name: value.question[0].high_activity_card_title,
          value: 'High',
          image: value.question[0].high_activity_image,
          description: value.question[0].high_activity_card_description,
        },
      ],
    };
  });

export const allergies = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    allergyQuestionPrompt: yup.string(),
    allergyQuestionSubheadline: yup.string(),
    labelName: yup.string(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      allergyQuestionSubheadline: value.question?.[0].subheadline,
      allergyQuestionPrompt: value.question?.[0].prompt,
      labelName: value.question?.[0].labelName || 'Allergy',
    };
  });

export const allergySelection = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    allergiesQuestionPrompt: yup.string(),
    allergiesQuestionCaption: yup.string(),
    allergiesSearchLabel: yup.string(),
  })
  .transform(value => {
    return {
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      allergiesQuestionPrompt: value.question?.[0].prompt,
      allergiesQuestionCaption: value.question?.[0].caption,
      allergySearchLabel: value.question?.[0].labelName || 'Other',
    };
  });

export const crunchingNumbers = yup
  .object({
    ollieLogo: nullableImageFromCMSSchema,
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    images: yup.array(requiredImageFromCMSSchema),
    slideDuration: yup.number(),
    captions: yup.array(),
    headline: yup.string(),
  })
  .transform(value => {
    return {
      ollieLogo: value.ollieLogo,
      mobileImage: value.mobileImage,
      desktopImage: value.desktopImage,
      images: value.question[0].images,
      captions: value.question[0].captions.split('/'),
      slideDuration: Number(value.question[0].slideDuration),
      headline: value.question[0].headline,
    };
  });

export const extras = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    desktopCtaText: yup.string(),
    headline: yup.string(),
    subheadline: yup.string(),
    addonSections: yup.array(addonSectionSchema).required(),
    body: yup.array(yup.mixed()),
    modals: yup.array(addonModalSchema),
  })
  .transform(({ body, ...value }) => ({
    mobileImage: value.mobileImage,
    desktopCtaText: value.desktop_cta_text || 'Continue',
    desktopImage: value.desktopImage,
    headline: value.question[0].headline,
    subheadline: value.question[0].subheadline,
    addonSections: value.question[0].addon_sections,
    body,
    modals: value.question[0].modals,
  }));

export const userInfoSchema = yup
  .object({
    headline: yup.string(),
    subheadline: yup.string(),
    eyebrowText: yup.string(),
    emailLabel: yup.string().required(),
    firstNameLabel: yup.string().required(),
    ctaButtonText: yup.string(),
    image: requiredImageFromCMSSchema,
    eyebrowVariant: yup.string(),
  })
  .transform(value => {
    return {
      headline: value.question[0].headline,
      subheadline: value.question[0].subheadline,
      eyebrowText: value?.question[0]?.eyebrow_text || value?.eyebrow_text,
      emailLabel: value.question[0].email_label || 'Email Address',
      firstNameLabel: value.question[0].first_name_label || 'Your Name',
      ctaButtonText: value.question[0].continue || 'Continue',
      image: value.desktopImage,
      eyebrowVariant: value.question[0].eyebrowVariant || undefined,
    };
  });
